import React, { useEffect, useState } from "react";
import { Users2, Search, Phone, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDate } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal } from "@/components/common";

export default function Customers() {
  const { can, isOwner } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const canCreate = can("customers", "create") || isOwner;

  const load = async () => {
    setLoading(true);
    try { setList(await api.get("/customers", q ? { q } : {})); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <>
      <PageHeader title="Pelanggan (CRM)" subtitle={`${list.length} pelanggan`} icon={Users2}
        actions={canCreate && <Button onClick={() => setCreating(true)} data-testid="add-customer-btn"><Plus size={16} /> Pelanggan</Button>} />

      <Card className="p-3 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / telepon..." className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-300 outline-none focus:border-primary" data-testid="customer-search" />
        </div>
      </Card>

      {loading ? <Loading /> : !list.length ? (
        <EmptyState icon={Users2} title="Belum ada pelanggan" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((c) => (
            <Card key={c.id} className="p-4 cursor-pointer hover:border-primary" onClick={() => setDetail(c.id)} data-testid={`customer-${c.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold">{(c.name || "?").charAt(0).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-800 truncate">{c.name || "Tanpa Nama"}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-1"><Phone size={11} /> {c.phone || "-"}</div>
                </div>
              </div>
              <div className="flex justify-between mt-3 text-sm">
                <span className="text-slate-400">{c.order_count || 0} order</span>
                {c.total_spent != null && <span className="font-medium text-slate-700">{rupiah(c.total_spent)}</span>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {detail && <CustomerDetail id={detail} onClose={() => setDetail(null)} />}
      {creating && <CustomerCreate onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </>
  );
}

function CustomerDetail({ id, onClose }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/customers/${id}`).then(setC); }, [id]);
  return (
    <Modal open onClose={onClose} title={c?.name || "Detail Pelanggan"} maxWidth="max-w-lg">
      {!c ? <Loading /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs text-slate-400">Telepon</div><div className="text-slate-700">{c.phone || "-"}</div></div>
            <div><div className="text-xs text-slate-400">Total Belanja</div><div className="text-slate-700">{rupiah(c.total_spent || 0)}</div></div>
            <div><div className="text-xs text-slate-400">Jumlah Order</div><div className="text-slate-700">{c.order_count || 0}</div></div>
            <div><div className="text-xs text-slate-400">Piutang</div><div className="text-rose-600">{rupiah(c.outstanding || 0)}</div></div>
            {c.address && <div className="col-span-2"><div className="text-xs text-slate-400">Alamat</div><div className="text-slate-700">{c.address}</div></div>}
          </div>
          {(c.orders || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2">Riwayat Order</div>
              <div className="border border-slate-100 rounded-xl divide-y divide-slate-100 max-h-60 overflow-y-auto">
                {c.orders.map((o) => (
                  <div key={o.id} className="flex justify-between px-3 py-2 text-sm">
                    <span className="text-slate-600">{o.order_no} · {fmtDate(o.created_at)}</span>
                    <span className="font-medium">{rupiah(o.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function CustomerCreate({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    setBusy(true);
    try { await api.post("/customers", form); toast.success("Pelanggan dibuat"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Pelanggan Baru" maxWidth="max-w-md">
      <div className="space-y-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama" className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="cf-name" />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="No. Telepon" className="w-full h-10 px-3 rounded-xl border border-slate-300" />
        <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Alamat" rows={2} className="w-full px-3 py-2 rounded-xl border border-slate-300" />
        <Button className="w-full" onClick={save} disabled={busy} data-testid="cf-save">{busy ? "Menyimpan..." : "Simpan"}</Button>
      </div>
    </Modal>
  );
}
